'use client';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import Link from 'next/link';
import { useSearchParams, useRouter } from 'next/navigation';
import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';
import AppImage from '@/components/ui/AppImage';
import ClientLoginForm from './ClientLoginForm';
import VendorLoginForm from './VendorLoginForm';
import AdminLoginForm from './AdminLoginForm';
import { Users, Briefcase, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { isAppRole, type AppRole } from '@/lib/auth/types';
import { homeForRole, safeRedirectPath } from '@/lib/auth/utils';

type Role = AppRole;

const roles: { id: Role; label: string; icon: React.ElementType; color: string; desc: string }[] = [
  { id: 'client', label: 'Client Portal', icon: Users, color: 'var(--neon-gold)', desc: 'Manage your event, contracts & payments' },
  { id: 'vendor', label: 'Vendor Portal', icon: Briefcase, color: 'var(--neon-cyan)', desc: 'View jobs, accept offers & track payouts' },
  { id: 'admin', label: 'DJ Command Center', icon: ShieldCheck, color: 'var(--neon-purple)', desc: 'Admin access — DJ ExXtra only' },
];

const SPARKLE_DEGREES = [0, 72, 144, 216, 288];

export default function AuthScreen() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { user, loading, role, roleResolved } = useAuth();
  const roleParam = searchParams.get('role');
  const redirectParam = searchParams.get('redirect');
  const errorParam = searchParams.get('error');
  const initialRole: Role = isAppRole(roleParam) ? roleParam : 'client';

  const [activeRole, setActiveRole] = useState<Role>(initialRole);
  const [mounted, setMounted] = useState(false);
  const didRedirectRef = useRef(false);
  const didToastErrorRef = useRef(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (isAppRole(roleParam)) {
      setActiveRole(roleParam);
    }
  }, [roleParam]);

  useEffect(() => {
    if (didToastErrorRef.current || !errorParam) return;
    didToastErrorRef.current = true;
    if (errorParam === 'unauthorized') {
      toast.error('You do not have access to that area.');
    } else if (errorParam === 'auth_callback') {
      toast.error('Sign-in link expired or invalid. Please try again.');
    }
  }, [errorParam]);

  // Already signed in → send to redirect target or role home (once).
  // Skip after an intentional sign-out so the login screen stays put.
  useEffect(() => {
    if (loading || !roleResolved || !user || !role || didRedirectRef.current) return;

    if (typeof window !== 'undefined' && sessionStorage.getItem('auth:signed_out') === '1') {
      sessionStorage.removeItem('auth:signed_out');
      didRedirectRef.current = true;
      return;
    }

    didRedirectRef.current = true;
    router.replace(safeRedirectPath(redirectParam, homeForRole(role)));
  }, [user, loading, role, roleResolved, router, redirectParam]);

  const sparklePositions = useMemo(
    () =>
      SPARKLE_DEGREES.map((deg) => ({
        deg,
        top: `calc(50% + ${Math.sin((deg * Math.PI) / 180) * 72}px - 4px)`,
        left: `calc(50% + ${Math.cos((deg * Math.PI) / 180) * 72}px - 4px)`,
        animationDelay: `${deg * 0.004}s`,
      })),
    []
  );

  return (
    <div className="min-h-screen bg-background flex overflow-hidden">
      {/* Ambient lights */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="light-burst w-96 h-96 bg-primary top-[-10%] left-[-5%]" />
        <div className="light-burst w-80 h-80 bg-accent top-[40%] right-[-5%]" />
        <div className="light-burst w-64 h-64" style={{ background: 'var(--neon-purple)', bottom: '10%', left: '20%' }} />
      </div>

      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 relative flex-col items-center justify-center p-12 overflow-hidden">
        {/* Background image with overlay */}
        <div className="absolute inset-0">
          <AppImage
            src="/assets/images/inbound3311747870800730922-1785334307248.jpg"
            alt="DJ ExXtra Maximum Effort promotional image with gold branding and concert lighting"
            fill
            className="object-cover opacity-30"
            priority
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(10,10,15,0.85) 0%, rgba(10,10,15,0.6) 100%)' }} />
        </div>

        <div className="relative z-10 flex flex-col items-center text-center gap-8">
          {/* Spinning ring logo */}
          <div className="relative w-40 h-40 flex items-center justify-center float-animation">
            <div className="absolute inset-0 rounded-full border-2 border-dashed border-primary/50 spinning-ring" />
            <div className="absolute inset-3 rounded-full border border-primary/70 spinning-ring-slow" style={{ animationDirection: 'reverse' }} />
            <div className="absolute inset-1.5 rounded-full" style={{ border: '2px solid var(--neon-gold)', boxShadow: '0 0 25px rgba(255,215,0,0.5)' }} />
            {mounted && sparklePositions.map(({ deg, top, left, animationDelay }) => (
              <div
                key={`auth-sparkle-${deg}`}
                className="absolute w-2 h-2 rounded-full bg-primary sparkle-pulse"
                style={{ top, left, animationDelay }}
              />
            ))}
            <div className="relative w-24 h-24 rounded-full overflow-hidden border-2 border-primary glow-gold">
              <AppImage
                src="/assets/images/inbound3311747870800730922-1785334307248.jpg"
                alt="DJ ExXtra Maximum Effort shield logo"
                fill
                className="object-cover"
              />
            </div>
          </div>

          <div>
            <p className="text-xs font-mono tracking-[0.4em] text-muted-foreground uppercase mb-2">Maximum Effort</p>
            <h1 className="text-4xl font-extrabold mb-1">
              <span className="gold-gradient-text">DJ E<span className="text-5xl font-black">X</span>Xtra</span>
            </h1>
            <p className="text-muted-foreground text-base">Mobile DJ &amp; Karaoke Services</p>
            <p className="text-xs font-mono text-muted-foreground mt-1">Northwest Indiana · Est. 2020</p>
          </div>

          {/* Portal descriptions */}
          <div className="flex flex-col gap-3 w-full max-w-xs">
            {roles.map((role) => {
              const RoleIcon = role.icon;
              const isActive = activeRole === role.id;
              return (
                <div
                  key={`portal-desc-${role.id}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-200 ${isActive ? 'border-opacity-60' : 'border-border bg-card/30'}`}
                  style={isActive ? { borderColor: role.color, background: `${role.color}10` } : {}}
                >
                  <RoleIcon size={16} style={{ color: role.color }} />
                  <div className="text-left">
                    <p className="text-sm font-semibold text-foreground">{role.label}</p>
                    <p className="text-xs text-muted-foreground">{role.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex flex-col gap-1 text-center">
            <a href="tel:2196149534" className="text-sm font-mono text-muted-foreground hover:text-primary transition-colors">219-614-9534</a>
            <a href="mailto:maximumeffortmdj@gmail.com" className="text-xs font-mono text-muted-foreground hover:text-primary transition-colors">maximumeffortmdj@gmail.com</a>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="w-full lg:w-1/2 flex flex-col items-center justify-center p-6 lg:p-12 relative z-10">
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-3 mb-8">
          <AppLogo size={36} />
          <div>
            <p className="text-xs font-mono text-muted-foreground">Maximum Effort</p>
            <p className="font-bold gold-gradient-text">DJ E<span className="text-lg font-black">X</span>Xtra</p>
          </div>
        </div>

        <div className="w-full max-w-md">
          {/* Role selector tabs */}
          <div className="flex rounded-xl overflow-hidden border border-border mb-6 p-1 bg-muted/30 gap-1">
            {roles.map((role) => {
              const RoleIcon = role.icon;
              const isActive = activeRole === role.id;
              return (
                <button
                  key={`role-tab-${role.id}`}
                  onClick={() => setActiveRole(role.id)}
                  className={`flex-1 flex flex-col items-center gap-1 py-2.5 px-2 rounded-lg text-xs font-semibold transition-all duration-200 ${
                    isActive ? 'text-black' : 'text-muted-foreground hover:text-foreground'
                  }`}
                  style={isActive ? { background: role.color } : {}}
                >
                  <RoleIcon size={14} />
                  <span className="hidden sm:block leading-none">{role.label.split(' ')[0]}</span>
                </button>
              );
            })}
          </div>

          {/* Form area */}
          <div className="rounded-2xl card-glass neon-border-gold p-6">
            {activeRole === 'client' && <ClientLoginForm />}
            {activeRole === 'vendor' && <VendorLoginForm />}
            {activeRole === 'admin' && <AdminLoginForm />}
          </div>

          {/* Back to site */}
          <div className="text-center mt-5">
            <Link href="/" className="text-xs text-muted-foreground hover:text-primary transition-colors font-mono">
              ← Back to maximumeffortmdj.com
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}